// Platform console → Requirements: the product backlog that staff feedback
// is converted into (routes/platform/feedback.js). Each requirement shows how
// many pieces of feedback — and how many hospitals — asked for it. Marking a
// requirement Done resolves the feedback linked to it.
import { useEffect, useState } from 'react';
import { platformApi } from '../lib/platformApi/client';
import { toCsv, downloadCsv } from '../lib/csv';
import { FEEDBACK_STATUS } from './FeedbackInboxScreen';
import type { Priority, ProductRequirement, RequirementStatus } from './types';

const STATUS: Record<RequirementStatus, { label: string; bg: string; color: string }> = {
  proposed:    { label: 'Proposed',    bg: '#F3F4F6', color: '#374151' },
  accepted:    { label: 'Accepted',    bg: '#DBEAFE', color: '#1E40AF' },
  in_progress: { label: 'In progress', bg: '#FEF3C7', color: '#92400E' },
  done:        { label: 'Done',        bg: '#DCFCE7', color: '#166534' },
  rejected:    { label: 'Rejected',    bg: '#FEE2E2', color: '#991B1B' },
};
const PRIORITY_COLOR: Record<Priority, string> = { critical: '#B91C1C', high: '#C2410C', medium: '#374151', low: '#6B7280' };
const PRIORITIES: Priority[] = ['critical', 'high', 'medium', 'low'];

const ghost = { background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' } as const;
const errorBox = { padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 12 } as const;
function message(e: unknown) { return e instanceof Error ? e.message : String(e); }

function RequirementDetail({ id, onChanged }: { id: number; onChanged: (r: ProductRequirement) => void }) {
  const [req, setReq] = useState<ProductRequirement | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    platformApi.get<ProductRequirement>(`/requirements/${id}`)
      .then(r => { setReq(r); setTitle(r.title); setDescription(r.description || ''); })
      .catch(e => setError(message(e)));
  }, [id]);

  if (error) return <div style={errorBox}>{error}</div>;
  if (!req) return <div style={{ padding: 12, color: '#9CA3AF', fontSize: 13 }}>Loading…</div>;

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await platformApi.patch<ProductRequirement>(`/requirements/${id}`, { title, description });
      setReq(prev => (prev ? { ...prev, ...r } : r)); onChanged(r);
    } catch (e) { setError(message(e)); }
    setSaving(false);
  };

  return (
    <div style={{ padding: '12px 14px 16px', background: '#FAFAFA', borderTop: '1px solid #F3F4F6' }}>
      <label className="lbl">Title</label>
      <input value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
      <label className="lbl" style={{ marginTop: 8 }}>Description</label>
      <textarea value={description} onChange={e => setDescription(e.target.value)} rows={5} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn-primary" disabled={saving || !title.trim() || (title === req.title && description === (req.description || ''))} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, margin: '12px 0 6px' }}>Linked feedback ({req.feedback?.length ?? 0})</div>
      {(req.feedback ?? []).map(f => (
        <div key={f.id} style={{ fontSize: 12.5, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
          <span style={{ fontFamily: 'var(--mono)', color: '#6B7280' }}>{f.ref}</span>{' '}
          <span style={{ background: FEEDBACK_STATUS[f.status].bg, color: FEEDBACK_STATUS[f.status].color, borderRadius: 20, padding: '0 6px', fontSize: 10.5, fontWeight: 600 }}>
            {FEEDBACK_STATUS[f.status].label}
          </span>{' '}
          {f.tenant_name} · {f.user_name || 'Staff'}{f.user_role ? ` (${f.user_role})` : ''}: {f.title || f.message || 'Voice feedback'}
        </div>
      ))}
    </div>
  );
}

export default function RequirementsScreen({ onBack, onOpenFeedback }: { onBack: () => void; onOpenFeedback: () => void }) {
  const [filter, setFilter] = useState<'open' | RequirementStatus | ''>('open');
  const [rows, setRows] = useState<ProductRequirement[] | null>(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPriority, setNewPriority] = useState<Priority>('medium');

  const load = () => {
    platformApi.get<ProductRequirement[]>(`/requirements${filter ? `?status=${filter}` : ''}`)
      .then(r => { setRows(r); setError(''); })
      .catch(e => setError(message(e)));
  };
  useEffect(load, [filter]);

  const patch = async (id: number, body: Partial<Pick<ProductRequirement, 'status' | 'priority'>>) => {
    try {
      const r = await platformApi.patch<ProductRequirement>(`/requirements/${id}`, body);
      setRows(list => list?.map(x => (x.id === id ? r : x)) ?? null);
    } catch (e) { setError(message(e)); }
  };

  const create = async () => {
    try {
      await platformApi.post('/requirements', { title: newTitle, description: newDescription, priority: newPriority });
      setCreating(false); setNewTitle(''); setNewDescription(''); setNewPriority('medium');
      load();
    } catch (e) { setError(message(e)); }
  };

  const exportCsv = () => {
    if (!rows) return;
    const csv = toCsv(rows.map(r => ({ ...r, status: STATUS[r.status].label, created_at: new Date(r.created_at).toLocaleDateString() })), [
      ['ref', 'Ref'], ['title', 'Title'], ['priority', 'Priority'], ['status', 'Status'], ['module', 'Module'],
      ['tenant_name', 'First requested by'], ['feedback_count', 'Feedback'], ['tenant_count', 'Hospitals'],
      ['description', 'Description'], ['created_at', 'Created'],
    ]);
    downloadCsv(`requirements-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
      <button onClick={onBack} style={{ marginBottom: 12, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', padding: 0 }}>← Back to tenants</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>Requirements</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Built from staff feedback. Marking one Done resolves the feedback behind it.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={ghost} onClick={onOpenFeedback}>💬 Feedback</button>
          <button style={ghost} onClick={exportCsv} disabled={!rows?.length}>⬇ Export CSV</button>
          <button className="btn-primary" onClick={() => setCreating(v => !v)}>+ New requirement</button>
        </div>
      </div>

      {creating && (
        <div className="card" style={{ padding: 16, marginBottom: 14 }}>
          <label className="lbl">Title</label>
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)} maxLength={200} />
          <label className="lbl" style={{ marginTop: 8 }}>Description</label>
          <textarea value={newDescription} onChange={e => setNewDescription(e.target.value)} rows={3} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 8 }}>
            <div>
              <label className="lbl">Priority</label>
              <select value={newPriority} onChange={e => setNewPriority(e.target.value as Priority)}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <button className="btn-primary" style={{ marginLeft: 'auto' }} disabled={!newTitle.trim()} onClick={create}>Create</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {([['open', 'Open'], ...Object.entries(STATUS).map(([k, v]) => [k, v.label]), ['', 'All']] as [string, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id as typeof filter)}
            style={{ ...ghost, padding: '4px 12px', fontSize: 12, background: filter === id ? '#111827' : 'var(--bg)', color: filter === id ? '#fff' : undefined }}>
            {label}
          </button>
        ))}
      </div>

      {error && <div style={errorBox}>{error}</div>}

      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
              <th style={{ padding: '10px 14px' }}>Ref</th>
              <th style={{ padding: '10px 14px' }}>Requirement</th>
              <th style={{ padding: '10px 14px' }}>Priority</th>
              <th style={{ padding: '10px 14px' }}>Status</th>
              <th style={{ padding: '10px 14px' }}>Asked by</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>Loading…</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>No requirements yet — convert feedback into one from the Feedback inbox.</td></tr>}
            {rows?.map(r => (
              <FragmentRow key={r.id} r={r} expanded={expanded === r.id}
                onToggle={() => setExpanded(e => (e === r.id ? null : r.id))}
                onPatch={(body) => patch(r.id, body)}
                onChanged={(u) => setRows(list => list?.map(x => (x.id === u.id ? { ...x, ...u } : x)) ?? null)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FragmentRow({ r, expanded, onToggle, onPatch, onChanged }: {
  r: ProductRequirement; expanded: boolean; onToggle: () => void;
  onPatch: (body: Partial<Pick<ProductRequirement, 'status' | 'priority'>>) => void;
  onChanged: (r: ProductRequirement) => void;
}) {
  return (
    <>
      <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
        <td style={{ padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12, color: '#6B7280', cursor: 'pointer' }} onClick={onToggle}>{r.ref}</td>
        <td style={{ padding: '10px 14px', cursor: 'pointer' }} onClick={onToggle}>
          <div style={{ fontWeight: 600 }}>{expanded ? '▾' : '▸'} {r.title}</div>
          {r.module && <div style={{ fontSize: 11.5, color: '#6B7280' }}>{r.module}</div>}
        </td>
        <td style={{ padding: '10px 14px' }}>
          <select value={r.priority} onChange={e => onPatch({ priority: e.target.value as Priority })}
            style={{ width: 'auto', color: PRIORITY_COLOR[r.priority], fontWeight: 600, padding: '2px 6px' }}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </td>
        <td style={{ padding: '10px 14px' }}>
          <select value={r.status} onChange={e => onPatch({ status: e.target.value as RequirementStatus })}
            style={{ width: 'auto', background: STATUS[r.status].bg, color: STATUS[r.status].color, fontWeight: 600, padding: '2px 6px', border: 'none' }}>
            {(Object.keys(STATUS) as RequirementStatus[]).map(s => <option key={s} value={s}>{STATUS[s].label}</option>)}
          </select>
        </td>
        <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151' }}>
          {r.feedback_count} feedback · {r.tenant_count} hospital{r.tenant_count === 1 ? '' : 's'}
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={5} style={{ padding: 0 }}><RequirementDetail id={r.id} onChanged={onChanged} /></td></tr>
      )}
    </>
  );
}
